import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { AuthProvider } from "./context/AuthContext";
import { EventProvider } from "./context/EventContext";
import { Welcome } from "./pages/Welcome";
import { EventList } from "./pages/EventList";
import { Layout } from "./components/Layout/Layout";
import { NewEvent } from "./pages/NewEvent";
import { Register } from "./pages/Register";
import { CatchAllRoute } from "./components/ProtectedRoute";
import { VolunteerHomepage } from "./pages/VolunteerHomepage";
import { MsalProvider } from "@azure/msal-react";
import { OrganisationHomepage } from "./pages/OrganisationHomepage";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { cs } from "date-fns/locale/cs";
import { AlertProvider } from "./context/AlertContext";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { OrganisationDocuments } from "./pages/OrganisationDocuments";
import { OrganisationEvidence } from "./pages/OrganisationEvidence";
import { UniversalVolunteerDetail } from "./pages/UniversalVolunteerDetail";
import { UniversalEventDetail } from "./pages/UniversalEventDetail";
import { UniversalOrganisationDetail } from "./pages/UniversalOrganisationDetail";
import { useInitializeEnums } from "./hooks/useInitializeEnums";
import { IPublicClientApplication } from "@azure/msal-browser";

interface AppProps {
  instance: IPublicClientApplication;
}

const App = ({ instance }: AppProps) => {
  // Initialize Zustand enum store
  useInitializeEnums();
  const theme = createTheme({
    palette: {
      primary: {
        main: "#8A2BE2",
      },
      secondary: {
        main: "#FFD700",
      },
    },
  });

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <AlertProvider>
          <MsalProvider instance={instance}>
            <AuthProvider>
              <EventProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={cs}
                >
                  <Routes>
                    <Route path="/login" element={<Welcome />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/register/:key" element={<Register />} />

                    {/* Universal Routes - accessible to all with different data based on auth */}
                    <Route element={<Layout />}>
                      <Route
                        path="/volunteer/:volunteerId"
                        element={<UniversalVolunteerDetail />}
                      />
                      <Route
                        path="/event/:eventId"
                        element={<UniversalEventDetail />}
                      />
                      <Route
                        path="/organisation/:organisationId"
                        element={<UniversalOrganisationDetail />}
                      />
                      <Route
                        path="/volunteer"
                        element={<VolunteerHomepage />}
                      />
                      <Route path="/volunteer/events" element={<EventList />} />
                      <Route
                        path="/volunteer/favorite"
                        element={<EventList />}
                      />
                      <Route
                        path="/organisation"
                        element={<OrganisationHomepage />}
                      />
                      <Route
                        path="/organisation/events"
                        element={<EventList />}
                      />
                      <Route
                        path="/organisation/new-event"
                        element={<NewEvent />}
                      />
                      <Route
                        path="/organisation/documents"
                        element={<OrganisationDocuments />}
                      />
                      <Route
                        path="/organisation/evidence"
                        element={<OrganisationEvidence />}
                      />
                    </Route>
                    <Route path="*" element={<CatchAllRoute />} />
                  </Routes>
                </LocalizationProvider>
              </EventProvider>
            </AuthProvider>
          </MsalProvider>
        </AlertProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
